'use client';
import React, { useState, useEffect } from 'react';

interface Experience {
  id: string;
  name: string;
  priceBase: number;
  priceTransport: number;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedExperience?: Experience | null;
  lang?: 'es' | 'en';
}

// Prices with 15% markup applied
const experiences: Experience[] = [
  { id: 'desayuno', name: 'Desayuno en el Cenote', priceBase: 1944, priceTransport: 1944 },
  { id: 'masaje', name: 'Masaje en el Cenote', priceBase: 3565, priceTransport: 3565 },
  { id: 'cena', name: 'Cena en el Cenote', priceBase: 2185, priceTransport: 2185 },
  { id: 'ritual', name: 'Ritual Maya en el Cenote', priceBase: 5865, priceTransport: 5865 },
];

const experiencesEn: Experience[] = [
  { id: 'desayuno', name: 'Cenote Breakfast', priceBase: 1944, priceTransport: 1944 },
  { id: 'masaje', name: 'Cenote Massage', priceBase: 3565, priceTransport: 3565 },
  { id: 'cena', name: 'Cenote Dinner', priceBase: 2185, priceTransport: 2185 },
  { id: 'ritual', name: 'Mayan Ritual at the Cenote', priceBase: 5865, priceTransport: 5865 },
];

const transportZones = {
  es: [
    { id: 'tulum', label: 'Dentro de Tulum', extra: 400 },
    { id: 'playa', label: 'Playa del Carmen', extra: 1200 },
    { id: 'cancun', label: 'Cancún', extra: 1800 },
  ],
  en: [
    { id: 'tulum', label: 'Inside Tulum', extra: 400 },
    { id: 'playa', label: 'Playa del Carmen', extra: 1200 },
    { id: 'cancun', label: 'Cancún', extra: 1800 },
  ],
};

const addonsMap: Record<string, { es: string[]; en: string[] }> = {
  desayuno: { es: ['Toallas Premium (+$200)', 'Batas de Baño (+$300)', 'Acceso a Nadar (+$150)'], en: ['Premium Towels (+$200)', 'Bath Robes (+$300)', 'Swimming Access (+$150)'] },
  masaje: { es: ['Toallas Premium (+$200)', 'Batas de Baño (+$300)', 'Chancletas (+$100)', 'Acceso a Nadar (+$150)'], en: ['Premium Towels (+$200)', 'Bath Robes (+$300)', 'Sandals (+$100)', 'Swimming Access (+$150)'] },
  cena: { es: ['Maridaje de Vinos (+$400)', 'Carta Extendida (+$200)', 'Flores y Velas (+$300)'], en: ['Wine Pairing (+$400)', 'Extended Menu (+$200)', 'Flowers & Candles (+$300)'] },
  ritual: { es: ['Upgrade Ritual de Parejas (+$1,500)', 'Flores y Velas (+$300)', 'Acceso a Nadar (+$150)'], en: ['Couples Ritual Upgrade (+$1,500)', 'Flowers & Candles (+$300)', 'Swimming Access (+$150)'] },
};

const labels = {
  es: {
    title: 'Reservar Experiencia',
    subtitle: 'Cenote Privado · Exclusivo',
    experience: 'Experiencia',
    transport: '¿Incluir Transporte?',
    transportZone: 'Zona de Recogida',
    pricePerPerson: 'Precio por persona:',
    transportNote: 'Incluye recogida y regreso desde tu hotel',
    name: 'Nombre Completo *',
    namePlaceholder: 'Tu nombre',
    email: 'Email *',
    phone: 'WhatsApp *',
    phonePlaceholder: '+52 999 000 0000',
    date: 'Fecha Preferida *',
    persons: 'Número de Personas',
    person: 'persona',
    persons2: 'personas',
    extras: 'Extras (opcional)',
    message: 'Mensaje Adicional',
    messagePlaceholder: 'Alergias, preferencias especiales, ocasión especial...',
    submit: 'Confirmar Reserva',
    submitting: 'Enviando...',
    successTitle: '¡Reserva Recibida!',
    successMsg: 'Te contactaremos en menos de 24 horas para confirmar tu experiencia.',
    successNote: 'Pago al confirmar. Aceptamos transferencia bancaria y tarjeta.',
    close: 'Cerrar',
    from: 'Desde',
    errorMsg: 'Hubo un error. Por favor contáctanos por WhatsApp.',
    // Summary step
    summaryTitle: 'Resumen de tu Reserva',
    summarySubtitle: 'Revisa los detalles antes de confirmar',
    summaryExperience: 'Experiencia',
    summaryDate: 'Fecha',
    summaryGuests: 'Personas',
    summaryTransport: 'Transporte',
    summaryExtras: 'Extras',
    summaryBasePrice: 'Precio base',
    summaryTransportPrice: 'Transporte',
    summaryExtrasPrice: 'Extras',
    summaryTotal: 'Total',
    summaryPerPerson: 'por persona',
    summaryNoTransport: 'Sin transporte',
    summaryNoExtras: 'Sin extras',
    summaryConfirm: 'Confirmar y Enviar',
    summaryBack: 'Volver a Editar',
    summaryNote: 'Pago al confirmar. Aceptamos transferencia bancaria y tarjeta.',
  },
  en: {
    title: 'Book Experience',
    subtitle: 'Private Cenote · Exclusive',
    experience: 'Experience',
    transport: 'Include Transport?',
    transportZone: 'Pick-up Zone',
    pricePerPerson: 'Price per person:',
    transportNote: 'Includes pick-up and drop-off from your hotel',
    name: 'Full Name *',
    namePlaceholder: 'Your name',
    email: 'Email *',
    phone: 'WhatsApp *',
    phonePlaceholder: '+52 999 000 0000',
    date: 'Preferred Date *',
    persons: 'Number of Guests',
    person: 'guest',
    persons2: 'guests',
    extras: 'Extras (optional)',
    message: 'Additional Message',
    messagePlaceholder: 'Allergies, special preferences, special occasion...',
    submit: 'Confirm Booking',
    submitting: 'Sending...',
    successTitle: 'Booking Received!',
    successMsg: 'We will contact you within 24 hours to confirm your experience.',
    successNote: 'Payment upon confirmation. We accept bank transfer and card.',
    close: 'Close',
    from: 'From',
    errorMsg: 'There was an error. Please contact us via WhatsApp.',
    // Summary step
    summaryTitle: 'Booking Summary',
    summarySubtitle: 'Review your details before confirming',
    summaryExperience: 'Experience',
    summaryDate: 'Date',
    summaryGuests: 'Guests',
    summaryTransport: 'Transport',
    summaryExtras: 'Extras',
    summaryBasePrice: 'Base price',
    summaryTransportPrice: 'Transport',
    summaryExtrasPrice: 'Extras',
    summaryTotal: 'Total',
    summaryPerPerson: 'per person',
    summaryNoTransport: 'No transport',
    summaryNoExtras: 'No extras',
    summaryConfirm: 'Confirm & Send',
    summaryBack: 'Back to Edit',
    summaryNote: 'Payment upon confirmation. We accept bank transfer and card.',
  },
};

// Helper to parse addon price from label string like "Premium Towels (+$200)"
function parseAddonPrice(addon: string): number {
  const match = addon.match(/\+\$([0-9,]+)/);
  if (!match) return 0;
  return parseInt(match[1].replace(',', ''), 10);
}

export default function BookingModal({ isOpen, onClose, selectedExperience, lang = 'en' }: BookingModalProps) {
  const lx = labels[lang];
  const expList = lang === 'es' ? experiences : experiencesEn;
  const zones = transportZones[lang];

  const [form, setForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha: '',
    personas: '2',
    experiencia: selectedExperience?.id || 'desayuno',
    transporte: false,
    transporteZona: 'tulum',
    addons: [] as string[],
    mensaje: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (selectedExperience) {
      setForm(prev => ({ ...prev, experiencia: selectedExperience.id }));
    }
  }, [selectedExperience]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSubmitted(false);
      setError(false);
      setShowSummary(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentExp = experiences.find(e => e.id === form.experiencia) || experiences[0];
  const currentExpDisplay = expList.find(e => e.id === form.experiencia) || expList[0];
  const currentAddons = addonsMap[form.experiencia]?.[lang] || [];
  const transportZone = zones.find(z => z.id === form.transporteZona);
  const transportExtra = form.transporte ? (transportZone?.extra || 0) : 0;
  const addonsTotal = form.addons.reduce((sum, addon) => sum + parseAddonPrice(addon), 0);
  const totalPrice = currentExp.priceBase + transportExtra + addonsTotal;

  const handleAddonToggle = (addon: string) => {
    setForm(prev => ({
      ...prev,
      addons: prev.addons.includes(addon)
        ? prev.addons.filter(a => a !== addon)
        : [...prev.addons, addon],
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSummary(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          precio: totalPrice,
          lang,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setShowSummary(false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-lux-black-soft border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-lux-black border-b border-white/10 px-7 py-5 flex items-center justify-between z-10">
          <div>
            <h2 className="font-serif text-lg font-light text-white tracking-wide">
              {showSummary ? lx.summaryTitle : lx.title}
            </h2>
            <p className="text-white/30 text-xs tracking-[0.2em] uppercase font-light mt-0.5">
              {showSummary ? lx.summarySubtitle : lx.subtitle}
            </p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 border border-lux-gold flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-lux-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-serif text-2xl text-white font-light mb-3">{lx.successTitle}</h3>
            <p className="text-white/40 mb-2 text-sm font-light">{lx.successMsg}</p>
            <p className="text-white/30 text-xs mb-8 font-light">{lx.successNote}</p>
            <button
              onClick={onClose}
              className="border border-white/20 text-white hover:bg-white hover:text-lux-black px-8 py-3 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300"
            >
              {lx.close}
            </button>
          </div>
        ) : showSummary ? (
          /* ── SUMMARY STEP ── */
          <div className="p-7 space-y-6">
            {/* Booking details */}
            <div className="border border-white/10 divide-y divide-white/10">
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryExperience}</span>
                <span className="text-white text-sm font-light">{currentExpDisplay.name}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryDate}</span>
                <span className="text-white text-sm font-light">{form.fecha || '—'}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryGuests}</span>
                <span className="text-white text-sm font-light">{form.personas} {Number(form.personas) === 1 ? lx.person : lx.persons2}</span>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryTransport}</span>
                <span className="text-white text-sm font-light">
                  {form.transporte ? transportZone?.label : lx.summaryNoTransport}
                </span>
              </div>
              <div className="flex items-start justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium mt-0.5">{lx.summaryExtras}</span>
                <div className="text-right">
                  {form.addons.length > 0 ? (
                    form.addons.map(addon => (
                      <div key={addon} className="text-white/70 text-xs font-light">{addon}</div>
                    ))
                  ) : (
                    <span className="text-white/40 text-sm font-light">{lx.summaryNoExtras}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="border border-white/10 divide-y divide-white/10">
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryBasePrice}</span>
                <span className="text-white/70 text-sm font-light">${currentExp.priceBase.toLocaleString()} MXN</span>
              </div>
              {form.transporte && (
                <div className="flex items-center justify-between px-5 py-3">
                  <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryTransportPrice}</span>
                  <span className="text-white/70 text-sm font-light">+${transportExtra.toLocaleString()} MXN</span>
                </div>
              )}
              {form.addons.length > 0 && (
                <div className="flex items-center justify-between px-5 py-3">
                  <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryExtrasPrice}</span>
                  <span className="text-white/70 text-sm font-light">+${addonsTotal.toLocaleString()} MXN</span>
                </div>
              )}
              <div className="flex items-center justify-between px-5 py-4 bg-white/5">
                <span className="text-white text-xs tracking-[0.2em] uppercase font-semibold">{lx.summaryTotal}</span>
                <div className="text-right">
                  <span className="font-serif text-lux-gold text-2xl font-light">${totalPrice.toLocaleString()}</span>
                  <span className="text-white/40 text-xs ml-1 font-sans">MXN</span>
                </div>
              </div>
            </div>

            <p className="text-white/25 text-xs font-light text-center">{lx.summaryNote}</p>

            {error && (
              <div className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-400 text-xs font-light">
                {lx.errorMsg}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowSummary(false); setError(false); }}
                className="flex-1 border border-white/20 text-white/60 hover:text-white hover:border-white/40 py-4 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300"
              >
                {lx.summaryBack}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="flex-1 bg-white text-lux-black hover:bg-lux-pearl py-4 text-xs font-medium tracking-[0.25em] uppercase transition-all duration-300 disabled:opacity-50"
              >
                {loading ? lx.submitting : lx.summaryConfirm}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleFormSubmit} className="p-7 space-y-6">
            {/* Experience selector */}
            <div>
              <label className="block text-white/50 text-xs tracking-[0.2em] uppercase font-medium mb-2">{lx.experience}</label>
              <select
                value={form.experiencia}
                onChange={e => setForm(prev => ({ ...prev, experiencia: e.target.value, addons: [] }))}
                className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200"
              >
                {expList.map(exp => (
                  <option key={exp.id} value={exp.id} className="bg-lux-black">{exp.name}</option>
                ))}
              </select>
            </div>

            {/* Transport toggle */}
            <div className="border border-white/10 p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white/60 text-xs tracking-[0.15em] uppercase font-medium">{lx.transport}</span>
                {/* Custom toggle — built with divs to avoid browser button quirks */}
                <div
                  role="switch"
                  aria-checked={form.transporte}
                  tabIndex={0}
                  onClick={() => setForm(prev => ({ ...prev, transporte: !prev.transporte }))}
                  onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setForm(prev => ({ ...prev, transporte: !prev.transporte })); } }}
                  className={`relative flex-shrink-0 cursor-pointer select-none transition-colors duration-200 ${form.transporte ? 'bg-lux-gold' : 'bg-white/20'}`}
                  style={{ width: 44, height: 24, borderRadius: 12 }}
                >
                  <div
                    className="absolute bg-white shadow-md transition-transform duration-200"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      top: 3,
                      left: 3,
                      transform: form.transporte ? 'translateX(20px)' : 'translateX(0px)',
                    }}
                  />
                </div>
              </div>

              {form.transporte && (
                <div className="mb-4">
                  <label className="block text-white/40 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.transportZone}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {zones.map(zone => (
                      <button
                        key={zone.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, transporteZona: zone.id }))}
                        className={`border px-3 py-2 text-xs font-light tracking-wide transition-all duration-200 ${
                          form.transporteZona === zone.id
                            ? 'border-lux-gold text-lux-gold' :'border-white/10 text-white/40 hover:border-white/30'
                        }`}
                      >
                        <div>{zone.label}</div>
                        <div className="text-lux-gold/70 text-xs mt-0.5">+${zone.extra.toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-white/30 mt-2 font-light">{lx.transportNote}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-white/30 text-xs font-light">{lx.pricePerPerson}</span>
                <span className="font-serif text-white text-xl font-light">
                  ${totalPrice.toLocaleString()}
                  <span className="text-xs text-white/30 ml-1 font-sans">MXN</span>
                </span>
              </div>
            </div>

            {/* Personal info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.name}</label>
                <input
                  type="text"
                  required
                  value={form.nombre}
                  onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder={lx.namePlaceholder}
                  className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200 placeholder:text-white/20"
                />
              </div>
              <div>
                <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.email}</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200 placeholder:text-white/20"
                />
              </div>
              <div>
                <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.phone}</label>
                <input
                  type="tel"
                  required
                  value={form.telefono}
                  onChange={e => setForm(prev => ({ ...prev, telefono: e.target.value }))}
                  placeholder={lx.phonePlaceholder}
                  className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200 placeholder:text-white/20"
                />
              </div>
              <div>
                <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.date}</label>
                <input
                  type="date"
                  required
                  value={form.fecha}
                  onChange={e => setForm(prev => ({ ...prev, fecha: e.target.value }))}
                  className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.persons}</label>
              <select
                value={form.personas}
                onChange={e => setForm(prev => ({ ...prev, personas: e.target.value }))}
                className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200"
              >
                {[1, 2].map(n => (
                  <option key={n} value={n} className="bg-lux-black">{n} {n === 1 ? lx.person : lx.persons2}</option>
                ))}
              </select>
            </div>

            {/* Add-ons */}
            {currentAddons.length > 0 && (
              <div>
                <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-3">{lx.extras}</label>
                <div className="space-y-2">
                  {currentAddons.map(addon => (
                    <label key={addon} className="flex items-center gap-3 cursor-pointer group">
                      <div
                        className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${form.addons.includes(addon) ? 'border-lux-gold bg-lux-gold' : 'border-white/20'}`}
                        onClick={() => handleAddonToggle(addon)}
                      >
                        {form.addons.includes(addon) && (
                          <svg className="w-2.5 h-2.5 text-lux-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-white/40 group-hover:text-white/70 transition-colors font-light">{addon}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-white/50 text-xs tracking-[0.15em] uppercase font-medium mb-2">{lx.message}</label>
              <textarea
                value={form.mensaje}
                onChange={e => setForm(prev => ({ ...prev, mensaje: e.target.value }))}
                placeholder={lx.messagePlaceholder}
                rows={3}
                className="w-full border border-white/10 bg-lux-black text-white px-4 py-3 text-sm font-light focus:outline-none focus:border-lux-gold transition-colors duration-200 resize-none placeholder:text-white/20"
              />
            </div>

            {/* Live Total Price Breakdown */}
            <div className="border border-white/10 divide-y divide-white/10">
              <div className="flex items-center justify-between px-5 py-3">
                <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryBasePrice}</span>
                <span className="text-white/70 text-sm font-light">${currentExp.priceBase.toLocaleString()} MXN</span>
              </div>
              {form.transporte && (
                <div className="flex items-center justify-between px-5 py-3">
                  <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryTransportPrice}</span>
                  <span className="text-white/70 text-sm font-light">+${transportExtra.toLocaleString()} MXN</span>
                </div>
              )}
              {form.addons.length > 0 && (
                <div className="flex items-center justify-between px-5 py-3">
                  <span className="text-white/40 text-xs tracking-[0.15em] uppercase font-medium">{lx.summaryExtrasPrice}</span>
                  <span className="text-white/70 text-sm font-light">+${addonsTotal.toLocaleString()} MXN</span>
                </div>
              )}
              <div className="flex items-center justify-between px-5 py-4 bg-white/5">
                <span className="text-white text-xs tracking-[0.2em] uppercase font-semibold">{lx.summaryTotal}</span>
                <div className="text-right">
                  <span className="font-serif text-lux-gold text-2xl font-light">${totalPrice.toLocaleString()}</span>
                  <span className="text-white/40 text-xs ml-1 font-sans">MXN</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-400 text-xs font-light">
                {lx.errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-lux-black hover:bg-lux-pearl py-4 text-xs font-medium tracking-[0.25em] uppercase transition-all duration-300 disabled:opacity-50"
            >
              {loading ? lx.submitting : lx.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
